// UI操作コントローラー
import type { AppState, AppError } from '../types/app.types.js';
import { ValidationUtils } from '../utils/ValidationUtils.js';
import { FileUtils } from '../utils/FileUtils.js';
import { ErrorHandler } from '../utils/ErrorHandler.js';

export class UIController {
  private state: AppState;
  private elements: Record<string, HTMLElement>;

  constructor(state: AppState, elements: Record<string, HTMLElement>) {
    this.state = state;
    this.elements = elements;
  }

  updateSizeDisplay(bytes: number): void {
    try {
      const display = this.elements.sizeDisplay;
      if (isNaN(bytes) || bytes <= 0) {
        display.textContent = ''; // 空の状態
      } else {
        display.textContent = ValidationUtils.formatFileSize(bytes);
      }
    } catch (error) {
      console.warn('サイズ表示の更新に失敗:', error);
    }
  }

  updateInputError(errorMessage: string | null): void {
    try {
      const errorElement = this.elements.sizeError;
      
      if (errorMessage) {
        errorElement.textContent = errorMessage;
        errorElement.style.display = 'block';
        errorElement.setAttribute('aria-live', 'polite');
      } else {
        errorElement.textContent = '';
        errorElement.style.display = 'none';
        errorElement.removeAttribute('aria-live');
      }
    } catch (error) {
      console.error('入力エラー表示の更新に失敗:', error);
    }
  }

  updateGenerateButton(): void {
    try {
      const button = this.elements.generateBtn as HTMLButtonElement;
      const isGenerating = this.state.generation.status === 'generating';
      
      button.disabled = !this.state.input.isValid || isGenerating;
      button.setAttribute('aria-busy', isGenerating.toString());
      
      if (isGenerating) {
        button.innerHTML = '<span class="material-icons btn-icon">hourglass_empty</span><span class="btn-text">生成中...</span>';
        button.setAttribute('aria-label', '画像生成中です');
      } else {
        button.innerHTML = '<span class="material-icons btn-icon">add_photo_alternate</span><span class="btn-text">画像を生成</span>';
        button.setAttribute('aria-label', '画像を生成する');
      }
    } catch (error) {
      console.error('生成ボタンの更新に失敗:', error);
    }
  }

  updateProgress(): void {
    try {
      const progressBar = this.elements.progressBar as HTMLElement;
      const progressPercentage = this.elements.progressPercentage;
      const currentPhase = this.elements.currentPhase;

      if (progressBar) {
        progressBar.style.width = `${this.state.generation.progress}%`;
      }
      
      if (progressPercentage) {
        progressPercentage.textContent = `${this.state.generation.progress}%`;
      }
      
      if (this.state.generation.currentPhase && currentPhase) {
        currentPhase.textContent = this.state.generation.currentPhase;
      }

      // ARIA属性の更新
      const progressContainer = progressBar?.parentElement;
      if (progressContainer) {
        progressContainer.setAttribute('aria-valuenow', this.state.generation.progress.toString());
        progressContainer.setAttribute('aria-valuetext', 
          `${this.state.generation.progress}% - ${this.state.generation.currentPhase || '処理中'}`);
      }
    } catch (error) {
      console.error('進捗表示の更新に失敗:', error);
    }
  }

  updateProcessingTimeGuidance(targetSize: number): void {
    try {
      const processingMethodElement = this.elements.processingMethod;
      const methodValueElement = this.elements.methodValue;

      if (!methodValueElement) {
        return;
      }

      // サイズベースの処理時間案内
      let timeGuidance = '';
      if (targetSize < 1024 * 1024) { // 1MB未満
        timeGuidance = '数秒で完了予定';
      } else if (targetSize < 10 * 1024 * 1024) { // 10MB未満
        timeGuidance = '10-30秒程度で完了予定';
      } else if (targetSize < 50 * 1024 * 1024) { // 50MB未満
        timeGuidance = '30秒-1分程度で完了予定';
      } else { // 50MB以上
        timeGuidance = '1分程度かかる場合があります';
      }
      
      methodValueElement.textContent = timeGuidance;
      
      if (processingMethodElement) {
        processingMethodElement.style.display = 'block';
      }
    } catch (error) {
      console.error('処理時間案内の更新に失敗:', error);
    }
  }

  updateResultDisplay(): void {
    try {
      if (!this.state.result.metadata) return;

      const { targetSize, actualSize, processingTime } = this.state.result.metadata;

      const targetSizeValue = this.elements.targetSizeValue;
      const actualSizeValue = this.elements.actualSizeValue;
      const processingTimeValue = this.elements.processingTimeValue;

      if (targetSizeValue) {
        targetSizeValue.textContent = `${targetSize.toLocaleString()}バイト`;
      }
      
      if (actualSizeValue) {
        actualSizeValue.textContent = `${actualSize.toLocaleString()}バイト`;
      }
      
      if (processingTimeValue) {
        // ミリ秒を秒に変換して表示
        const processingTimeInSeconds = processingTime / 1000;
        processingTimeValue.textContent = `${processingTimeInSeconds.toFixed(1)}秒`;
      }

      // 精度バッジの更新
      const accuracyBadge = this.elements.accuracyBadge;
      if (accuracyBadge) {
        const sizeDifference = Math.abs(targetSize - actualSize);
        if (sizeDifference === 0) {
          accuracyBadge.textContent = '±0バイト';
          accuracyBadge.className = 'accuracy-badge perfect';
          accuracyBadge.setAttribute('aria-label', '完璧な精度で生成されました');
        } else {
          accuracyBadge.textContent = `±${sizeDifference}バイト`;
          accuracyBadge.className = 'accuracy-badge';
          accuracyBadge.setAttribute('aria-label', `${sizeDifference}バイトの誤差があります`);
        }
      }
    } catch (error) {
      console.error('結果表示の更新に失敗:', error);
    }
  }

  updateUI(): void {
    try {
      // セクションの表示/非表示
      const progressSection = this.elements.progressSection;
      if (progressSection) {
        progressSection.style.display = 
          this.state.generation.status === 'generating' ? 'block' : 'none';
      }
      
      const resultSection = this.elements.resultSection;
      if (resultSection) {
        resultSection.style.display = 
          this.state.generation.status === 'success' ? 'block' : 'none';
      }
      
      const errorSection = this.elements.errorSection;
      if (errorSection) {
        errorSection.style.display = 
          this.state.error.hasError ? 'block' : 'none';
      }

      // 結果表示の更新
      if (this.state.generation.status === 'success' && this.state.result.metadata) {
        this.updateResultDisplay();
      }

      // ボタン状態の更新
      this.updateGenerateButton();

      // アニメーション効果
      this.addFadeInAnimation();
    } catch (error) {
      console.error('UI更新に失敗:', error);
      // UI更新に失敗した場合は基本的な状態に戻す
      this.resetToSafeState();
    }
  }

  showError(appError: AppError): void {
    try {
      // エラー状態を更新
      this.state.error.hasError = true;
      this.state.error.error = appError;
      this.state.generation.status = 'error';
      
      // エラー履歴に追加
      const errorHandler = ErrorHandler.getInstance();
      if (!errorHandler.isDuplicateError(appError)) {
        this.state.error.errorHistory.push(appError);
        
        // 履歴サイズの制限
        if (this.state.error.errorHistory.length > this.state.error.maxHistorySize) {
          this.state.error.errorHistory = this.state.error.errorHistory.slice(-this.state.error.maxHistorySize);
        }
      }
      
      // エラーメッセージの表示
      const errorMessage = this.elements.errorMessage;
      if (errorMessage) {
        errorMessage.textContent = appError.message;
        errorMessage.setAttribute('role', 'alert');
        errorMessage.setAttribute('aria-live', 'assertive');
      }
      
      // 提案の表示
      const errorSuggestion = this.elements.errorSuggestion;
      if (appError.suggestion && errorSuggestion) {
        errorSuggestion.textContent = appError.suggestion;
        errorSuggestion.style.display = 'block';
      } else if (errorSuggestion) {
        errorSuggestion.style.display = 'none';
      }

      // 重要度によるクラス設定
      const errorSection = this.elements.errorSection;
      if (errorSection) {
        errorSection.className = `error-section severity-${appError.severity}`;
      }
      
      // リトライボタンの表示制御
      const retryBtn = this.elements.retryBtn as HTMLButtonElement;
      if (retryBtn && appError.retryable) {
        retryBtn.style.display = 'inline-flex';
        retryBtn.innerHTML = '<span class="material-icons btn-icon">refresh</span><span class="btn-text">リトライ</span>';
      } else if (retryBtn) {
        retryBtn.style.display = 'none';
      }

      this.updateUI();
    } catch (error) {
      console.error('エラー表示に失敗:', error);
      // フォールバック: シンプルなアラート
      alert(`エラーが発生しました: ${appError.message}`);
    }
  }

  hideError(): void {
    try {
      this.state.error.hasError = false;
      this.state.error.error = null;
      this.updateUI();
    } catch (error) {
      console.error('エラー非表示に失敗:', error);
    }
  }

  handleDownload(): void {
    try {
      if (!this.state.result.blob) {
        throw new Error('ダウンロード可能なファイルがありません');
      }

      const blob = this.state.result.blob;
      const metadata = this.state.result.metadata;
      
      if (!metadata) {
        throw new Error('メタデータがありません');
      }

      FileUtils.downloadBlob(
        blob, 
        `image-${metadata.targetSize}bytes-${Date.now()}.jpg`
      );
    } catch (error) {
      console.error('ダウンロードに失敗:', error);
      const errorHandler = ErrorHandler.getInstance();
      this.showError(errorHandler.createUnknownError(error));
    }
  }

  handleGenerateAnother(): void {
    try {
      this.resetState();
      this.updateUI();
      
      // 入力フォームにフォーカス
      const inputElement = this.elements.targetSizeInput as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    } catch (error) {
      console.error('新規生成の準備に失敗:', error);
    }
  }

  handleErrorClose(): void {
    try {
      this.hideError();
    } catch (error) {
      console.error('エラー閉じる処理に失敗:', error);
    }
  }

  handleDebugToggle(): void {
    try {
      this.state.ui.showDebugInfo = !this.state.ui.showDebugInfo;
      
      const debugContent = this.elements.debugContent;
      if (this.state.ui.showDebugInfo) {
        debugContent.style.display = 'block';
        this.updateDebugInfo();
      } else {
        debugContent.style.display = 'none';
      }
    } catch (error) {
      console.error('デバッグ表示切り替えに失敗:', error);
    }
  }

  showErrorStats(): void {
    try {
      const errorHandler = ErrorHandler.getInstance();
      const stats = errorHandler.getErrorStats();
      
      const debugContent = this.elements.debugContent;
      debugContent.innerHTML = `
        <h4>エラー統計</h4>
        <p>総エラー数: ${stats.total}</p>
        <p>最近のエラー: ${stats.recentCount}</p>
        <details>
          <summary>タイプ別統計</summary>
          <ul>
            ${Object.entries(stats.byType).map(([type, count]) => 
              `<li>${type}: ${count}件</li>`
            ).join('')}
          </ul>
        </details>
        <details>
          <summary>重要度別統計</summary>
          <ul>
            ${Object.entries(stats.bySeverity).map(([severity, count]) => 
              `<li>${severity}: ${count}件</li>`
            ).join('')}
          </ul>
        </details>
      `;
    } catch (error) {
      console.error('エラー統計表示に失敗:', error);
    }
  }

  private resetState(): void {
    this.state.input.targetSize = null;
    this.state.input.isValid = false;
    this.state.input.errorMessage = null;
    
    this.state.generation.status = 'idle';
    this.state.generation.progress = 0;
    this.state.generation.currentPhase = null;
    this.state.generation.estimatedTime = null;
    
    this.state.result.blob = null;
    this.state.result.actualSize = null;
    this.state.result.accuracy = null;
    this.state.result.processingTime = null;
    this.state.result.metadata = null;
    
    this.hideError();
  }

  private resetToSafeState(): void {
    try {
      // 基本的な表示状態に戻す
      this.elements.progressSection.style.display = 'none';
      this.elements.resultSection.style.display = 'none';
      this.elements.errorSection.style.display = 'none';
      
      // ボタンを有効化
      const generateBtn = this.elements.generateBtn as HTMLButtonElement;
      generateBtn.disabled = false;
      generateBtn.innerHTML = '<span class="material-icons btn-icon">add_photo_alternate</span><span class="btn-text">画像を生成</span>';
    } catch (error) {
      console.error('セーフ状態への復帰に失敗:', error);
    }
  }

  private updateDebugInfo(): void {
    try {
      const debugContent = this.elements.debugContent;
      debugContent.innerHTML = `
        <pre>${JSON.stringify({
          input: this.state.input,
          generation: {
            ...this.state.generation,
            estimatedTime: this.state.generation.estimatedTime
          },
          result: {
            actualSize: this.state.result.actualSize,
            accuracy: this.state.result.accuracy,
            processingTime: this.state.result.processingTime
          },
          ui: this.state.ui,
          error: {
            hasError: this.state.error.hasError,
            errorCode: this.state.error.error?.code,
            errorType: this.state.error.error?.type
          }
        }, null, 2)}</pre>
      `;
    } catch (error) {
      console.error('デバッグ情報更新に失敗:', error);
      this.elements.debugContent.textContent = 'デバッグ情報の取得に失敗しました';
    }
  }

  private addFadeInAnimation(): void {
    try {
      const visibleSections = [
        this.elements.progressSection,
        this.elements.resultSection,
        this.elements.errorSection
      ].filter(section => section.style.display !== 'none');

      visibleSections.forEach(section => {
        section.classList.remove('fade-in');
        // 強制的にリフローを発生させる
        section.offsetHeight;
        section.classList.add('fade-in');
      });
    } catch (error) {
      console.warn('アニメーション適用に失敗:', error);
    }
  }
} 